// src/components/dashboard/HolidayCalendar.js - UPDATED with Calendar Integration
import { Calendar, Download, Edit2, ExternalLink, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useBusiness } from '../../hooks/useBusiness';
import { useHolidays } from '../../hooks/useHolidays';
import CalendarIntegration from './CalendarIntegration';

const HolidayCalendar = () => {
  const { businessProfile } = useBusiness();
  const { 
    holidays, 
    loading, 
    error: hookError, 
    addHoliday, 
    updateHoliday, 
    deleteHoliday,
    generateICSContent 
  } = useHolidays();
  
  // Local UI state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('integration'); // Start with calendar integration

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    type: 'closed',
    recurring: false,
    notes: ''
  });

  // Combine hook error with local error
  const displayError = error || hookError;

  // Form handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Validate form data
      if (!formData.name || !formData.startDate) {
        setError('Holiday name and start date are required');
        return;
      }

      if (formData.endDate && formData.startDate > formData.endDate) {
        setError('End date must be after start date');
        return;
      }

      const holidayData = {
        ...formData,
        endDate: formData.endDate || formData.startDate, // Default to single day
      };

      let result;
      if (editingHoliday) {
        // Update existing holiday
        result = await updateHoliday(editingHoliday.id, holidayData);
        if (result.success) {
          setSuccess('Holiday updated successfully');
        }
      } else {
        // Add new holiday
        result = await addHoliday(holidayData);
        if (result.success) {
          setSuccess('Holiday added successfully');
        }
      }

      if (!result.success) {
        setError(result.error || 'Failed to save holiday');
        return;
      }

      // Reset form
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
        type: 'closed',
        recurring: false,
        notes: ''
      });
      setShowAddForm(false);
      setEditingHoliday(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError('Failed to save holiday');
      console.error('Error saving holiday:', err);
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData(holiday);
    setShowAddForm(true);
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Are you sure you want to delete this holiday?')) {
      return;
    }

    try {
      const result = await deleteHoliday(holidayId);
      if (result.success) {
        setSuccess('Holiday deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to delete holiday');
      }
    } catch (err) {
      setError('Failed to delete holiday');
      console.error('Error deleting holiday:', err);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingHoliday(null);
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      type: 'closed',
      recurring: false,
      notes: ''
    });
    setError('');
  };

  const generateICSDownload = () => {
    if (!businessProfile) return;
    
    // Use the hook's method to generate ICS content
    const icsContent = generateICSContent(businessProfile.businessName);

    // Create download
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${businessProfile.businessName.replace(/\s+/g, '_')}_calendar.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'integration', label: 'Calendar Integration', icon: ExternalLink },
    { id: 'holidays', label: 'Holidays & Closures', icon: Calendar }
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar Management</h1>
          <p className="text-gray-600">Manage your Google Calendar integration and business closures</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {displayError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {displayError}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow border">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="mr-2 h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Calendar Integration Tab */}
          {activeTab === 'integration' && (
            <div>
              <CalendarIntegration />
              
              {/* Additional Calendar Features */}
              <div className="mt-8 grid md:grid-cols-2 gap-6">
                {/* Calendar Export */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Export Business Calendar</h3>
                  <p className="text-gray-600 mb-4">
                    Download your business holidays and closures as an .ics file that can be imported into any calendar application.
                  </p>
                  <button
                    onClick={generateICSDownload}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download .ics File
                  </button>
                </div>

                {/* Calendar Help */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-medium text-blue-900 mb-4">Need Help?</h3>
                  <div className="space-y-3 text-blue-800 text-sm">
                    <p>• Your calendar shows your availability to customers</p>
                    <p>• Holidays and closures prevent bookings on those dates</p>
                    <p>• Share the public link with customers</p>
                    <p>• Embed the calendar on your website</p>
                  </div>
                  <button className="mt-4 text-blue-700 hover:text-blue-800 text-sm font-medium">
                    View Setup Guide →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Holidays Tab */}
          {activeTab === 'holidays' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-medium text-gray-900">Holidays & Business Closures</h2>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Holiday
                </button>
              </div>

              {/* Add/Edit Holiday Form */}
              {showAddForm && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
                  </h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Holiday Name *
                        </label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Christmas Day"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          value={formData.type}
                          onChange={(e) => setFormData({...formData, type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="closed">Closed</option>
                          <option value="limited_hours">Limited Hours</option>
                          <option value="special_event">Special Event</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date *
                        </label>
                        <input
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date (optional)
                        </label>
                        <input
                          type="date"
                          value={formData.endDate}
                          onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min={formData.startDate}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes (optional)
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="Additional information about this holiday/closure"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="recurring"
                        checked={formData.recurring}
                        onChange={(e) => setFormData({...formData, recurring: e.target.checked})}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="recurring" className="ml-2 block text-sm text-gray-700">
                        Recurring yearly
                      </label>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                      >
                        {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Holidays List */}
              <div className="bg-white rounded-lg border border-gray-200">
                {holidays.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No holidays scheduled</h3>
                    <p className="text-gray-600 mb-4">Add holidays and closures to help customers know when you're unavailable.</p>
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Add Your First Holiday
                    </button>
                  </div>
                ) : (
                  <div className="p-6">
                    <div className="space-y-4">
                      {holidays.map((holiday) => (
                        <div key={holiday.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center">
                            <Calendar className="h-5 w-5 text-blue-600 mr-3" />
                            <div>
                              <h3 className="font-medium text-gray-900">
                                {holiday.name}
                                {holiday.recurring && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                    Yearly
                                  </span>
                                )}
                              </h3>
                              <p className="text-sm text-gray-600">
                                {holiday.startDate === holiday.endDate 
                                  ? new Date(holiday.startDate).toLocaleDateString()
                                  : `${new Date(holiday.startDate).toLocaleDateString()} to ${new Date(holiday.endDate).toLocaleDateString()}`
                                }
                              </p>
                              {holiday.notes && (
                                <p className="text-xs text-gray-500 mt-1">{holiday.notes}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              holiday.type === 'closed' ? 'bg-red-100 text-red-800' :
                              holiday.type === 'limited_hours' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {holiday.type.replace('_', ' ')}
                            </span>
                            <button
                              onClick={() => handleEdit(holiday)}
                              className="text-gray-400 hover:text-blue-600"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(holiday.id)}
                              className="text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HolidayCalendar;